// Horas de servicio de la bitácora. Por omisión sólo miden; si la compañía
// configuró límites (engrane → modal, PUT /company hos_limits), el reloj con
// límite dice "restan" y cambia de color. Las cuentas viven en hos.ts.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "../quickReports.compute";
import { fetchHosLimits, fetchQuickDaily, saveHosLimits } from "../quickReportsService";
import type { DailyUnitSummary } from "../types";
import { useUserStore } from "../../../store/userStore";
import {
  canEditHosLimits,
  FMCSA_EXAMPLE,
  fmtClock,
  HOS_LIMIT_KEYS,
  hasHosLimits,
  hosClocks,
  NO_HOS_LIMITS,
  parseLimitInput,
  type HosClock,
  type HosLevel,
  type HosLimits,
} from "./hos";

// Mismo plazo que la bitácora: si el servicio no contesta, "—" y no un anillo
// esperando para siempre.
export const QUICK_TIMEOUT_MS = 15_000;

// Sin límite (calc) cada reloj lleva el color de su estado, como en la barra
// del día; con límite, ámbar desde 80 % y rojo al pasar.
const STATE_STROKE: Record<HosClock["key"], string> = {
  drive: "stroke-green-500",
  window: "stroke-blue-500", // azul y no primario: con el tema Dumax (verde) chocaba con Manejo
  rest: "stroke-indigo-500",
  week: "stroke-gray-400",
  cycle: "stroke-gray-400",
};
const STATE_BG: Record<HosClock["key"], string> = {
  drive: "bg-green-500",
  window: "bg-blue-500",
  rest: "bg-indigo-500",
  week: "bg-gray-400",
  cycle: "bg-gray-400",
};
const levelStroke = (c: HosClock) =>
  c.level === "warn" ? "stroke-amber-500" : c.level === "over" ? "stroke-red-500" : c.level === "met" ? "stroke-green-500" : STATE_STROKE[c.key];
const levelBg = (c: HosClock) =>
  c.level === "warn" ? "bg-amber-500" : c.level === "over" ? "bg-red-500" : c.level === "met" ? "bg-green-500" : STATE_BG[c.key];
const LEVEL_TEXT: Partial<Record<HosLevel, string>> = { warn: "text-amber-500", over: "text-red-500", met: "text-green-500" };

// Sólo con límite: cuánto falta o cuánto se pasó, en h:mm como el reloj.
const footnote = (c: HosClock, failed: boolean) => {
  if (c.usedS == null) return failed ? "Sin datos" : "Calculando…";
  if (c.remainingS == null) return null;
  if (c.key === "rest") return c.remainingS <= 0 ? "Mínimo cumplido" : `Faltan ${fmtClock(c.remainingS)} h`;
  return c.remainingS < 0 ? `Excedido ${fmtClock(-c.remainingS)} h` : `Restan ${fmtClock(c.remainingS)} h`;
};

const R = 34;
const CIRC = 2 * Math.PI * R;

function Ring({ c, dark, sub }: { c: HosClock; dark: boolean; sub?: string }) {
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const foot = footnote(c, false);
  return (
    <li className="flex flex-col items-center text-center flex-shrink-0 w-[5.75rem]">
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90" aria-hidden>
          <circle cx="40" cy="40" r={R} fill="none" strokeWidth="7" className={dark ? "stroke-white/10" : "stroke-black/[0.07]"} />
          <circle
            cx="40"
            cy="40"
            r={R}
            fill="none"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - c.pct)}
            className={`${levelStroke(c)} motion-safe:transition-[stroke-dashoffset,stroke] motion-safe:duration-[250ms] motion-safe:ease-out`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-base font-semibold leading-none tabular-nums ${dark ? "text-white" : "text-gray-900"}`}>{fmtClock(c.usedS ?? 0)}</span>
          <span className={`mt-1 text-[10px] tabular-nums ${muted}`}>{c.limitS != null ? `de ${c.limitS / 3600} h` : "h"}</span>
        </div>
      </div>
      <p className={`mt-1.5 text-xs font-medium leading-tight ${dark ? "text-gray-200" : "text-gray-800"}`}>{c.label}</p>
      {sub && <p className={`text-[10px] leading-tight ${muted}`}>{sub}</p>}
      {foot && <p className={`text-[11px] tabular-nums ${LEVEL_TEXT[c.level] ?? muted}`}>{foot}</p>}
    </li>
  );
}

// Semana y 8 días: una cifra (contra 7 u 8 × 24 h el anillo no dice nada) y,
// si la compañía puso límite semanal, una barra contra ese límite.
function Figure({ c, dark, failed }: { c: HosClock; dark: boolean; failed: boolean }) {
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const foot = footnote(c, failed);
  return (
    <div className={`rounded-lg px-3 py-2 ${dark ? "bg-white/5" : "bg-black/[0.03]"}`}>
      <p className={`text-[11px] leading-tight ${muted}`}>{c.label}</p>
      <p className={`mt-0.5 leading-none ${dark ? "text-white" : "text-gray-900"}`}>
        <span className="text-lg font-semibold tabular-nums">{c.usedS == null ? "—" : fmtClock(c.usedS)}</span>
        <span className={`ml-1 text-xs ${muted}`}>{c.limitS != null ? `h de ${c.limitS / 3600} h` : "h"}</span>
      </p>
      {c.limitS != null && c.usedS != null && (
        <div className={`mt-1.5 h-1.5 rounded-full overflow-hidden ${dark ? "bg-white/10" : "bg-black/[0.07]"}`} aria-hidden>
          <div className={`h-full rounded-full ${levelBg(c)} motion-safe:transition-[width] motion-safe:duration-200 motion-safe:ease-out`} style={{ width: `${c.pct * 100}%` }} />
        </div>
      )}
      {foot && <p className={`mt-0.5 text-[11px] tabular-nums ${LEVEL_TEXT[c.level] ?? muted}`}>{foot}</p>}
    </div>
  );
}

const FIELDS: { key: keyof HosLimits; label: string }[] = [
  { key: "driving_day_h", label: "Manejo por día" },
  { key: "duty_day_h", label: "Jornada por día (primer a último movimiento)" },
  { key: "rest_min_h", label: "Descanso mínimo por día" },
  { key: "driving_week_h", label: "Manejo por semana (lun–dom)" },
];
const toText = (l: HosLimits) => Object.fromEntries(HOS_LIMIT_KEYS.map((k) => [k, l[k] == null ? "" : String(l[k])])) as Record<keyof HosLimits, string>;

function LimitsDialog({ open, onClose, limits, companyId, dark }: { open: boolean; onClose: () => void; limits: HosLimits; companyId: string; dark: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const [text, setText] = useState(() => toText(limits));
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setText(toText(limits));
      d.showModal();
    } else if (!open && d.open) d.close();
  }, [open, limits]);
  const parsed = FIELDS.map((f) => ({ ...f, ...parseLimitInput(f.key, text[f.key]) }));
  const invalid = parsed.some((p) => p.error);
  const save = useMutation({
    mutationFn: () => saveHosLimits(companyId, Object.fromEntries(parsed.map((p) => [p.key, p.value])) as HosLimits),
    onSuccess: (saved) => {
      qc.setQueryData(["company-hos-limits", companyId], saved);
      onClose();
    },
  });
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const field = dark ? "bg-dark-surface border-dark-border text-white" : "bg-white border-gray-300 text-gray-900";
  const ghost = `px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
    dark ? "text-gray-300 hover:bg-white/10" : "text-gray-700 hover:bg-black/5"
  }`;
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby="hos-limits-title"
      className={`w-[min(28rem,calc(100vw-2rem))] rounded-2xl border p-0 shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm open:motion-safe:animate-scaleIn ${
        dark ? "bg-dark-card border-dark-border text-white" : "bg-white border-gray-200 text-gray-900"
      }`}
    >
      <form
        method="dialog"
        className="p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid) save.mutate();
        }}
      >
        <h2 id="hos-limits-title" className="text-base font-semibold">
          Límites de horas de servicio
        </h2>
        <p className={`mt-1 text-xs ${muted}`}>
          Opcionales y para toda la compañía. Un campo vacío no limita: el reloj sólo calcula. Con límite, el reloj dice cuánto resta y se pone ámbar
          desde el 80 % y rojo al pasarlo.
        </p>
        <div className="mt-4 flex flex-col gap-3">
          {parsed.map((p) => (
            <label key={p.key} className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">{p.label}</span>
              <span className="flex items-center gap-1.5 flex-shrink-0">
                <input
                  type="text"
                  inputMode="decimal"
                  value={text[p.key]}
                  onChange={(e) => setText((t) => ({ ...t, [p.key]: e.target.value }))}
                  placeholder="—"
                  aria-invalid={!!p.error}
                  aria-describedby={p.error ? `hos-err-${p.key}` : undefined}
                  className={`w-20 h-9 rounded-md border px-2.5 text-right tabular-nums outline-none focus:ring-2 focus:ring-primary-500 ${field} ${
                    p.error ? "border-red-500" : ""
                  }`}
                />
                <span className={`text-xs ${muted}`}>h</span>
              </span>
              {p.error && (
                <span id={`hos-err-${p.key}`} className="sr-only">
                  {p.error}
                </span>
              )}
            </label>
          ))}
          {parsed
            .filter((p) => p.error)
            .map((p) => (
              <p key={p.key} className="text-xs text-red-500" aria-hidden>
                {p.label}: {p.error}
              </p>
            ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <button type="button" className={`text-xs font-medium ${dark ? "text-primary-300" : "text-primary-600"} hover:underline`} onClick={() => setText(toText(FMCSA_EXAMPLE))}>
            Usar referencia EE. UU. (FMCSA)
          </button>
          <button type="button" className={`text-xs font-medium ${muted} hover:underline`} onClick={() => setText(toText(NO_HOS_LIMITS))}>
            Quitar todos (sólo calcular)
          </button>
        </div>
        {save.isError && <p className="mt-3 text-xs text-red-500">No se pudieron guardar los límites. Inténtalo de nuevo.</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className={ghost} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            disabled={invalid || save.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white disabled:opacity-50 transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

export default function HosClocks({
  day,
  companyId,
  date,
  tz,
  isToday,
  isDarkMode,
}: {
  day: DailyUnitSummary;
  companyId: string;
  date: string;
  tz: string;
  isToday: boolean;
  isDarkMode: boolean;
}) {
  // Semana lun–dom y 8 días: D-7..D de la unidad (cubre cualquier lunes). Con
  // from ≠ to el backend manda sólo totales.
  const cycle = useQuery({
    queryKey: ["quick-reports", "hos-cycle", companyId, day.device_id, date, tz],
    queryFn: ({ signal }) => fetchQuickDaily({ companyId, from: addDays(date, -7), to: date, tz, deviceIds: [day.device_id] }, signal, QUICK_TIMEOUT_MS),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const limitsQ = useQuery({
    queryKey: ["company-hos-limits", companyId],
    queryFn: ({ signal }) => fetchHosLimits(companyId, signal),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const limits = limitsQ.data ?? NO_HOS_LIMITS;
  const user = useUserStore((s) => s.user);
  const canEdit =
    limitsQ.isSuccess &&
    canEditHosLimits(user?.role_id || localStorage.getItem("userRoleId") || "", user?.company_id || localStorage.getItem("userCompanyId") || "", companyId);
  const [editing, setEditing] = useState(false);

  const { clocks, carriesOver } = hosClocks(day, cycle.isError ? null : (cycle.data?.items ?? null), limits, tz, isToday);
  const [drive, duty, rest, week, eight] = clocks;
  const dark = isDarkMode;
  const muted = dark ? "text-gray-400" : "text-gray-500";

  return (
    <section aria-label="Horas de servicio" className="min-w-0 xl:w-[18rem] xl:flex-shrink-0 xl:sticky xl:top-0">
      <div className="flex items-start gap-2 mb-2">
        <div className="min-w-0">
          <h3 className={`text-[11px] font-semibold uppercase tracking-wider ${dark ? "text-gray-500" : "text-gray-400"}`}>Horas de servicio</h3>
          <p className={`text-[11px] ${muted}`}>{hasHosLimits(limits) ? "Con límites de la compañía" : "Sólo cálculo, sin límites"}</p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Configurar límites de horas de servicio"
            title="Configurar límites"
            className={`ml-auto w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              dark ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-black/5 hover:text-gray-900"
            }`}
          >
            <i className="ri-settings-3-line text-base" aria-hidden />
          </button>
        )}
      </div>
      {/* Móvil: fila con scroll horizontal. Escritorio: tres en la columna lateral. */}
      <ul className="flex gap-2 overflow-x-auto pb-1 xl:grid xl:grid-cols-3 xl:overflow-visible">
        <Ring c={drive} dark={dark} />
        <Ring c={duty} dark={dark} sub="primer a último movimiento" />
        <Ring c={rest} dark={dark} />
      </ul>
      {carriesOver && (
        <p className={`mt-2 inline-flex items-center gap-1.5 text-[11px] ${muted}`}>
          <i className="ri-moon-line" aria-hidden />
          Jornada continúa del día anterior
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Figure c={week} dark={dark} failed={cycle.isError} />
        <Figure c={eight} dark={dark} failed={cycle.isError} />
      </div>
      {cycle.isError && (
        <button type="button" onClick={() => cycle.refetch()} className={`mt-2 text-xs font-medium ${dark ? "text-primary-300" : "text-primary-600"} hover:underline`}>
          No se pudo calcular la semana · Reintentar
        </button>
      )}
      {canEdit && <LimitsDialog open={editing} onClose={() => setEditing(false)} limits={limits} companyId={companyId} dark={dark} />}
    </section>
  );
}
